import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSocket, useSocketEvent } from "socket.io-react-hook";
import { _getToken } from "src/Config/axiosApi";
import { AUTH_DISABLED } from "src/App/Providers";
import { PositionsGroupedByPair } from "../types";
import { usePositionsSnapshot } from "../api/usePositionsSnapshot";

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

interface PositionsSnapshot {
  type: "POSITIONS_SNAPSHOT" | "POSITIONS_UPDATE";
  grouped: PositionsGroupedByPair[];
  stats: PositionStats;
  timestamp: number;
}

export interface GroupLiveContracts {
  kalshi: number;
  poly: number;
  total: number;
}

export interface GroupLivePosition {
  groupId: string;
  /**
   * `null` when the group does not name both venue accounts, so its legs were
   * never attributed and no total can be stated. Such a group used to be
   * omitted from the payload entirely; it now ARRIVES carrying the unknown,
   * which must render as such rather than be summed or sorted as `0`.
   */
  descalceTotal: number | null;
  contractsBought: GroupLiveContracts;
  /** The group names fewer than two venue accounts. `contractsBought` is then
   *  a PARTIAL count over the half that IS named. */
  accountsUnresolved?: boolean;
  computedAt: number;
}

interface GroupLivePositionEvent {
  groups: GroupLivePosition[];
  computedAt: number;
}

interface PositionsContextProps {
  groups: PositionsGroupedByPair[];
  unmatched: PositionsGroupedByPair[];
  stats: PositionStats | null;
  isConnected: boolean;
  triggerSync: () => void;
  isSyncing: boolean;
  groupLive: Record<string, GroupLivePosition>;
}

const PositionsContext = createContext<PositionsContextProps | undefined>(
  undefined
);

export const PositionsProvider = ({ children }: { children: ReactNode }) => {
  const accessToken = _getToken();

  const [groups, setGroups] = useState<PositionsGroupedByPair[]>([]);
  const [unmatched, setUnmatched] = useState<PositionsGroupedByPair[]>([]);
  const [stats, setStats] = useState<PositionStats | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [groupLive, setGroupLive] = useState<
    Record<string, GroupLivePosition>
  >({});

  const { socket } = useSocket(
    import.meta.env.VITE_PREDICTIONS_WS_URL + "/predictions",
    {
      enabled: AUTH_DISABLED || !!accessToken,
      auth: (cb: (data: { token: string }) => void) =>
        cb({ token: _getToken() }),
    }
  );

  const { lastMessage: snapshotMsg } = useSocketEvent<PositionsSnapshot>(
    socket,
    "positions_snapshot"
  );

  const { lastMessage: updateMsg } = useSocketEvent<PositionsSnapshot>(
    socket,
    "positions_update"
  );

  // Per-group live trading snapshot (descalce + contract counts), delivered on
  // the SAME `/predictions` socket — no second connection.
  const { lastMessage: groupLiveMsg } =
    useSocketEvent<GroupLivePositionEvent>(socket, "group_live_position");

  const hasSubscribed = useRef(false);

  useEffect(() => {
    if (!socket.connected) {
      hasSubscribed.current = false;
      return;
    }
    if (!hasSubscribed.current) {
      socket.emit("subscribe_predictions");
      hasSubscribed.current = true;
    }
  }, [socket.connected]);

  const applyData = useCallback(
    (msg: { grouped?: PositionsGroupedByPair[]; stats?: PositionStats | null }) => {
      if (!msg?.grouped) return;
      setGroups(msg.grouped.filter((g) => g._id != null));
      setUnmatched(msg.grouped.filter((g) => g._id == null));
      if (msg.stats) setStats(msg.stats);
      setIsSyncing(false);
    },
    []
  );

  // Seed cache via REST — see api/usePositionsSnapshot.ts for the why.
  const { data: restSnapshot } = usePositionsSnapshot(
    AUTH_DISABLED || !!accessToken
  );

  useEffect(() => {
    if (restSnapshot) applyData(restSnapshot);
  }, [restSnapshot, applyData]);

  useEffect(() => {
    if (snapshotMsg) applyData(snapshotMsg);
  }, [snapshotMsg, applyData]);

  useEffect(() => {
    if (updateMsg) applyData(updateMsg);
  }, [updateMsg, applyData]);

  useEffect(() => {
    if (!groupLiveMsg?.groups) return;
    setGroupLive((prev) => {
      const next = { ...prev };
      for (const g of groupLiveMsg.groups) {
        if (g?.groupId) next[g.groupId] = g;
      }
      return next;
    });
  }, [groupLiveMsg]);

  const triggerSync = useCallback(() => {
    setIsSyncing(true);
    socket.emit("sync_positions");
  }, [socket]);

  const isConnected = socket.connected;

  return (
    <PositionsContext.Provider
      value={{
        groups,
        unmatched,
        stats,
        isConnected,
        triggerSync,
        isSyncing,
        groupLive,
      }}
    >
      {children}
    </PositionsContext.Provider>
  );
};

export const usePositions = () => {
  const context = useContext(PositionsContext);
  if (!context) {
    throw new Error("usePositions must be used within a PositionsProvider");
  }
  return context;
};

/**
 * Latest live trading snapshot (descalce + contract counts) for one opener
 * group, from the `group_live_position` socket event. `undefined` until the
 * first snapshot for that group arrives (or when `groupId` is absent).
 */
export const useGroupLivePosition = (
  groupId: string | null | undefined
): GroupLivePosition | undefined => {
  const { groupLive } = usePositions();
  if (!groupId) return undefined;
  return groupLive[groupId];
};
